/**
 * merchant_lots.ts — the PURE premium district of the Merchant Hive
 * (2026-08-29): the GREAT SQUARE at spawn and the rentable store lots
 * around it. Architectural constants, not seeded — every client and the
 * server agree on the exact same 172 lots.
 *
 * MIRROR: server/src/lib/merchant_lots.ts ⇄ client/src/lib/merchant_lots.ts
 * (byte-identical below the header).
 *
 * Geometry: two crescent rings of stores FACING the square (doors on local
 * -Z, yaw = atan2(x, z) points the door at the origin) plus four grand
 * emporiums on the diagonals. Four 22 m boulevards leave the square at the
 * cardinals; ring lots skip the boulevard mouths. The old instanced hive
 * yields everything inside HIVE_PREMIUM_R to this district.
 */

export type LotTier = 'boutique' | 'gallery' | 'grand';

export interface PremiumLot {
  key: string;
  tier: LotTier;
  x: number;
  z: number;
  /** Door on local -Z faces the plaza. */
  yaw: number;
  w: number;
  d: number;
  h: number;
}

/** The great square's radius (matches the old plaza clear zone). */
export const HIVE_SQUARE_R = 84;
/** The premium district's outer edge — the instanced hive starts past it. */
export const HIVE_PREMIUM_R = 300;
export const BOULEVARD_HALF_W = 11;

export const LOT_RENT: Record<LotTier, bigint> = {
  boutique: 150n,
  gallery: 300n,
  grand: 800n,
};
export const LOT_RENT_DAYS = 7;
export const LOT_MAX_PER_RENTER = 2;
export const LOT_LISTING_CAP = 12;
/** Platform cut of every sale (basis points). */
export const MERCHANT_FEE_BPS = 250n;

const LOT_DIMS: Record<LotTier, { w: number; d: number; h: number }> = {
  boutique: { w: 12, d: 10, h: 6 },
  gallery: { w: 16, d: 12, h: 7.5 },
  grand: { w: 26, d: 18, h: 10 },
};

/** All 172 premium lots. Deterministic and constant. */
export function premiumLots(): PremiumLot[] {
  const lots: PremiumLot[] = [];
  const place = (key: string, tier: LotTier, r: number, thetaDeg: number): void => {
    const t = (thetaDeg * Math.PI) / 180;
    const x = Math.sin(t) * r;
    const z = Math.cos(t) * r;
    const dims = LOT_DIMS[tier];
    lots.push({ key, tier, x, z, yaw: Math.atan2(x, z), ...dims });
  };
  // Ring A — 16 boutiques at r 118, four per quadrant, clear of the boulevards.
  for (let i = 0; i < 16; i++) {
    const quadrant = Math.floor(i / 4);
    const withinDeg = 18 + (i % 4) * 18;            // 18°, 36°, 54°, 72°
    place(`ringA:${i}`, 'boutique', 118, quadrant * 90 + withinDeg);
  }
  // Ring B — 16 galleries at r 176, offset so they peer between the boutiques.
  for (let i = 0; i < 16; i++) {
    const quadrant = Math.floor(i / 4);
    const withinDeg = 13 + (i % 4) * 21.5;          // 13°, 34.5°, 56°, 77.5°
    place(`ringB:${i}`, 'gallery', 176, quadrant * 90 + withinDeg);
  }
  // The four GRAND EMPORIUMS command the diagonals at r 242.
  for (let i = 0; i < 4; i++) place(`grand:${i}`, 'grand', 242, 45 + i * 90);
  // Ring C — 16 more galleries at r 250, threaded between the emporiums.
  for (let i = 0; i < 16; i++) {
    const quadrant = Math.floor(i / 4);
    const withinDeg = [15, 30, 60, 75][i % 4];
    place(`ringC:${i}`, 'gallery', 250, quadrant * 90 + withinDeg);
  }
  // The GRAND AVENUES: shopping rows flanking each boulevard from r 320 to
  // r 880 — fifteen lots a side, doors facing the promenade, a grand
  // emporium anchoring every fifth position.
  const AVE_TIERS: LotTier[] = ['boutique', 'gallery', 'boutique', 'gallery', 'grand'];
  for (let a = 0; a < 4; a++) {
    const t = (a * Math.PI) / 2;
    const axis = { x: Math.sin(t), z: Math.cos(t) };
    const lateral = { x: Math.cos(t), z: -Math.sin(t) };
    for (const side of [-1, 1]) {
      for (let i = 0; i < 15; i++) {
        const tier = AVE_TIERS[i % AVE_TIERS.length];
        const dims = LOT_DIMS[tier];
        const r = 320 + i * 40;
        const off = BOULEVARD_HALF_W + dims.d / 2 + 3;
        const x = axis.x * r + lateral.x * off * side;
        const z = axis.z * r + lateral.z * off * side;
        // The door looks across the avenue (toward the boulevard axis).
        const doorDir = { x: -lateral.x * side, z: -lateral.z * side };
        lots.push({ key: `ave:${a}:${side === -1 ? 'l' : 'r'}:${i}`, tier, x, z, yaw: Math.atan2(-doorDir.x, -doorDir.z), ...dims });
      }
    }
  }
  return lots;
}

export function lotByKey(key: string): PremiumLot | null {
  for (const l of premiumLots()) if (l.key === key) return l;
  return null;
}
